import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import cx from 'classnames';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { DateTime, Duration } from 'luxon';

import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import ConvertIcon from '@mui/icons-material/OfflineBolt';
import EditIcon from '@mui/icons-material/Edit';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';

import { allevaOrangeColor } from '../md/assets/jss/material-dashboard-pro-react';

import Modal from './Modal';
import CrmRadio from './CrmRadio';
import CrmTextField from './CrmTextField';

import SearchIcon from '@mui/icons-material/Search';

import { alpha } from '@mui/material/styles';

import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';

import {
  updateCallLogFilter,
  setIntendedReferrerContactId,
  setIntendedReferrerCompanyId,
  setCaseIntendedClientId,
  setIntendedEmergencyContactId,
  setIntendedClientId,
  setInitialCallFormPrimingInfo,
} from '../store/slices/intendedInfoSlice';
import { setPropertyFilterState } from '../store/slices/filterStatesSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  dateStringForDateTime,
  timeStringForDateTime,
  convertToUserStdPhoneNumber,
  isNonLocalNonLoggedInVisitor,
  showAlert,
  logWithTime,
  createStdRequest,
  apiFetch,
} from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';
import SocketAccess from '../comm/SocketAccess';

import CrmButton from './CrmButton';
import AlertModal from './AlertModal';
import CallRailPlayerWidget from './CallRailPlayerWidget';
import NewInquiryModal from './NewInquiryModal';
import ColumnFilterIcon from './ColumnFilterIcon';
import KDatePicker from './KDatePicker';

import pageTableStyles from '../assets/jss/crm/components/pageTableStyles.jsx';
import tableHeadStyles from '../assets/jss/crm/components/tableHeadStyles.jsx';

function desc(a, b, orderBy) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];
  if (typeof aValue === 'string') {
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();
  }
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const columns = [
  { id: 'otherPersonName', numeric: false, label: 'Other Party', filterable: true },
  { id: 'date', numeric: false, label: 'Date/Time' },
  { id: 'otherNumber', numeric: false, label: 'Phone Number' },
  { id: 'direction', numeric: false, label: 'Direction', notSortable: true, filterable: true },
  { id: 'duration', numeric: false, label: 'Duration' },
  { id: 'playLink', numeric: false, label: 'Recording', notSortable: true },
  { id: 'handled', numeric: false, label: 'Action', notSortable: true },
];

function handleEntered() {
  document.getElementsByTagName('body')[0].style.paddingRight = '0px';
}

class CallTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  createFilterHandler = (property) => (event) => {
    return property === 'Direction'
      ? this.props.onShowDirectionFilter(event)
      : this.props.onShowUnknownCallerFilter(event);
  };

  render() {
    const { classes, order, orderBy, shouldHighlightFilterIcon } = this.props;

    return (
      <TableHead>
        <TableRow className={classes.headerRow}>
          {columns.map(
            (column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? 'right' : 'left'}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.filterable && (
                  <ColumnFilterIcon
                    onClick={this.createFilterHandler(column.label)}
                    shouldHighlight={() => shouldHighlightFilterIcon(column.id)}
                  />
                )}

                {column.label}

                {!column.notSortable && (
                  <Tooltip title="Sort" placement={column.numeric ? 'bottom-end' : 'bottom-start'} enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={order}
                      onClick={this.createSortHandler(column.id)}
                      classes={{
                        icon: orderBy === column.id ? classes.activeSortIcon : classes.inactiveSortIcon,
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}

CallTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
};

const StyledTableHead = withStyles(tableHeadStyles)(CallTableHead);

const appBarStyles = (theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    toolBar: {
      backgroundColor: '#cdf',
    },
    grow: {
      flexGrow: 1,
    },
    newItemButton: {
      margin: theme.spacing(1),
    },
    menuButton: {
      marginLeft: -12,
      marginRight: 20,
    },
    title: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
      fontFamily: 'MetricWebBold, sans-serif',
    },
    search: {
      position: 'relative',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: alpha(theme.palette.common.white, 0.15),
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
      },
      marginLeft: 0,
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
        width: 'auto',
      },
      '& div div': {
        zIndex: 3,
      },
    },
    searchIcon: {
      width: theme.spacing(9),
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    downloadIcon: {
      width: theme.spacing(10),
      height: '100%',
      position: 'absolute',
      right: '116px',
      top: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      '&:hover': {
        color: '#0c0dee',
      },
    },
    inputRoot: {
      color: 'inherit',
      width: '100%',
    },
    inputInput: {
      border: 0,
      color: 'currentColor',
      boxSizing: 'content-box',
      background: 'none',
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(10),
      transition: theme.transitions.create('width'),
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: 120,
        '&:focus': {
          width: 200,
        },
      },
    },
  });

function CallTableAppBar(props) {
  const {
    classes,
    summary,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    autocompleteOptions,
    onAutocompleteChange,
  } = props;
  return (
    <div className={classes.root}>
      <Toolbar className={classes.toolBar}>
        <Typography className={classes.title} variant="h5" color="inherit" noWrap>
          {summary}
        </Typography>
        <div className={classes.grow} />
        <KDatePicker label="Start" value={startDate} onChange={(newDate) => onStartDateChange(newDate)} />
        <div style={{ width: '20px' }} />
        <KDatePicker label="End" value={endDate} onChange={(newDate) => onEndDateChange(newDate)} />
        <div className={classes.grow} />
        <div className={classes.search}>
          {false && (
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
          )}
          <Autocomplete
            options={autocompleteOptions}
            getOptionLabel={(name) => name}
            style={{ width: 220 }}
            renderInput={(params) => <TextField {...params} label="Search" variant="outlined" />}
            onChange={(event, name) => onAutocompleteChange(name)}
          />
        </div>
      </Toolbar>
    </div>
  );
}

CallTableAppBar.propTypes = {
  classes: PropTypes.object.isRequired,
  autocompleteOptions: PropTypes.array.isRequired,
  summary: PropTypes.string.isRequired,
};

const StyledAppBar = withStyles(appBarStyles)(CallTableAppBar);

const styles = (theme) => ({
  ...pageTableStyles(theme),
  inactiveName: {
    color: '#777',
  },
  availableConvertIcon: {
    color: allevaOrangeColor[0],
  },
  grayedConvertIcon: {
    color: '#fdc',
  },
  noCallsNotice: {
    textAlign: 'center',
    fontSize: '200%',
  },
  phoneIcon: {
    display: 'inline-block',
    transform: 'translateY(5px)',
  },
  filterListItem: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  filterListLabel: {
    color: '#6b6b6b',
  },
});

class CallRailCallTable extends React.Component {
  state = {
    order: 'desc',
    orderBy: 'date',
    autocompleteValue: '',
    calls: [],
    shouldShowAlertModal: false,
    shouldShowUnknownCallerFilter: false,
    shouldShowDirectionFilter: false,
    showUnknownCallers: true,
    showInboundCalls: true,
    showOutboundCalls: true,
    alertModalText: '',
    otherPersonNameFilter: null,
    shouldShowConversionModal: false,
    shouldShowNoteModal: false,
    conversionType: 'lead',
    shouldShowNewInquiryModal: false,
    noteText: '',
    ...this.filterStateWithDateObjects(),
  };

  userPersonId = 0;
  callRailCallId = 0;
  noteId = 0;
  oldNoteText = '';
  viewingFirmId = 0;

  filterStateWithDateObjects() {
    const startDate = DateTime.fromISO(this.props.reduxFiltersState.startDate);
    const endDate = DateTime.fromISO(this.props.reduxFiltersState.endDate);
    return { ...this.props.reduxFiltersState, startDate, endDate };
  }

  now = DateTime.local();
  oneDayAsDuration = Duration.fromObject({ days: 1 });

  componentDidMount() {
    this.restoreDirectionFilterState();

    const outreachCallbackStruct = {
      kind: 'callRailCall',
      pageName: 'CallRailCallTable',
      refreshFunc: () => this.redisplayPageForNewCall(),
    };
    SocketAccess.registerRefreshCallback(outreachCallbackStruct);

    this.fetchDataAndDisplay();

    const userInfo = DA.getLoggedInUserInfo();
    this.userPersonId = userInfo.personId;
    this.viewingFirmId = DA.getFirmId();
  }

  componentWillUnmount() {
    SocketAccess.unregisterRefreshCallbacksForPage('CallRailCallTable');
  }

  sanityCheckDatesAndFetchDataAndDisplay() {
    if (this.sanityCheckDates()) {
      this.fetchDataAndDisplay();
    }
  }

  tomorrowAtThisTime = DateTime.local().plus({ days: 1 });
  aWeekFromNow = DateTime.local().plus({ days: 7 });
  startOf2019 = DateTime.fromISO('2019-01-01');

  sanityCheckDates() {
    const { startDate, endDate } = this.state;
    if (endDate.valueOf() > this.aWeekFromNow.valueOf()) {
      console.log(`endDate is too far in the future`);
      return false;
    }
    if (startDate.valueOf() > this.tomorrowAtThisTime.valueOf()) {
      console.log(`startDate is in the future`);
      this.setState({ calls: [] });
      return false;
    }
    if (startDate.valueOf() > endDate.valueOf()) {
      this.setState({ calls: [] });
      return false;
    }
    if (startDate.valueOf() < this.startOf2019.valueOf()) {
      console.log(`startDate is too far in the past`);
      return false;
    }
    return true;
  }

  fetchDataAndDisplay() {
    const { startDate, endDate } = this.state;
    const continuation = (calls) => {
      const callsWithNamesIncluded = calls.map((call) => {
        const otherPersonName = this.nameOfOtherPersonToCall(call);
        return { ...call, otherPersonName };
      });
      logWithTime(`fetchDataAndDisplay() produced ${callsWithNamesIncluded.length} calls`);
      this.setState({ calls: callsWithNamesIncluded });
    };
    ReduxLoader.loadCallRailCallsBetweenDates(this, startDate, endDate, continuation);
  }

  redisplayPageForNewCall() {
    logWithTime('In CallRailCallTable calling fetchDataAndDisplay() in hopes of showing new call');
    this.fetchDataAndDisplay();
  }

  restoreDirectionFilterState() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['callLogPage'];
    const nameValuePairs = pageFiltersState ? (pageFiltersState.direction ? pageFiltersState.direction : []) : [];
    const incomingPair = nameValuePairs.find((pair) => pair.name === 'incoming');
    const shouldShowIncoming = !incomingPair || incomingPair.value;
    const outgoingPair = nameValuePairs.find((pair) => pair.name === 'outgoing');
    const shouldShowOutgoing = !outgoingPair || outgoingPair.value;
    this.setState({
      showInboundCalls: shouldShowIncoming,
      showOutboundCalls: shouldShowOutgoing,
    });
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;

    let order = 'desc';
    if (this.state.orderBy === property) {
      order = this.state.order === 'desc' ? 'asc' : 'desc';
    }

    this.setState({ order, orderBy });
  };

  handleContactClick = (event, call) => {
    const { otherPersonId, callRailCallId } = call;
    const contact = DA.getPersonById(otherPersonId);
    if (contact) {
      const referralFirmId = this.findFirmAtWhichOtherPersonIsContact(call);
      if (referralFirmId) {
        this.props.setIntendedReferrerCompanyId(referralFirmId);
        this.props.setIntendedReferrerContactId(otherPersonId);
        this.setState({ redirect: 'referrerContact' });
      } else if (this.otherPersonIsEmergencyContact(call)) {
        const clientId = this.getClientForWhomPersonIsEmergencyContact(call);
        if (clientId === 0) {
          showAlert(this, `Cannot show page for ${call.otherPersonName}.`);
        } else if (clientId === -1) {
          let msg = `Cannot show page for ${call.otherPersonName} `;
          msg += 'because they are a contact for multiple clients.';
          showAlert(this, msg);
        } else {
          this.props.setIntendedEmergencyContactId(otherPersonId);
          this.props.setIntendedClientId(clientId);
          this.setState({ redirect: 'emergencyContact' });
        }
      } else if (this.otherPersonIsClient(call)) {
        this.props.setCaseIntendedClientId(otherPersonId);
        this.setState({ redirect: 'case' });
      } else {
        showAlert(this, `Cannot show page for ${call.otherPersonName}.`);
      }
    } else {
      const otherFirm = DA.getSenderFirmOfOutreachWithCallRailCall(callRailCallId);
      if (otherFirm) {
        this.props.setIntendedReferrerCompanyId(otherFirm.firmId);
        this.setState({ redirect: 'referrerOrg' });
      } else {
        showAlert(this, `Cannot show page for ${call.otherPersonName}.`);
      }
    }
  };

  findFirmAtWhichOtherPersonIsContact = (call) => {
    const { firmId, otherPersonId } = call;
    const visibleFirmIds = DA.getIdsOfFirmsVisibleToFirm(firmId);
    for (let i = 0; i < visibleFirmIds.length; i++) {
      const visibleFirmId = visibleFirmIds[i];
      const contactsAtFirm = DA.getIdsOfContactsAtVisibleFirm(visibleFirmId);
      if (contactsAtFirm.indexOf(otherPersonId) >= 0) {
        return visibleFirmId;
      }
    }
    return 0;
  };

  otherPersonIsClient = (call) => {
    const { firmId, otherPersonId } = call;
    const clientIds = DA.getIdsOfClientsOfFirm(firmId);
    return clientIds.indexOf(otherPersonId) >= 0;
  };

  otherPersonIsEmergencyContact = (call) => {
    const clientIds = DA.getIdsOfClientsWithEmergencyContactWithId(call.otherPersonId);
    return clientIds.length > 0;
  };

  getClientForWhomPersonIsEmergencyContact = (call) => {
    const { otherPersonId } = call;
    const clientIds = DA.getIdsOfClientsWithEmergencyContactWithId(otherPersonId);
    const nClients = clientIds.length;
    if (nClients === 0) {
      return 0;
    } else if (nClients > 1) {
      return -1;
    } /* just one client ffor whom this is the emergency contact, so it's unambigouos */ else {
      return clientIds[0];
    }
  };

  applyFilter(calls) {
    return calls.filter((call) => this.passFilter(call));
  }

  passFilter(call) {
    if (this.state.otherPersonNameFilter) {
      return this.passOtherPersonNameFilter(call);
    } else {
      return this.passDateFilter(call) && this.passDirectionFilter(call) && this.passUnknownCallerFilter(call);
    }
  }

  passOtherPersonNameFilter(call) {
    return call.otherPersonName === this.state.otherPersonNameFilter;
  }

  passDateFilter(call) {
    return (
      call.date.valueOf() >= this.state.startDate.valueOf() &&
      call.date.valueOf() < this.state.endDate.plus(this.oneDayAsDuration).valueOf()
    );
  }

  passDirectionFilter(call) {
    if (call.direction === 'inbound') {
      return this.state.showInboundCalls;
    } else {
      return this.state.showOutboundCalls;
    }
  }

  passUnknownCallerFilter(call) {
    return this.state.showUnknownCallers || DA.getPersonById(call.otherPersonId);
  }

  computeContactItemText(contactIds) {
    if (contactIds.length === 0) {
      return '<None listed yet>';
    } else {
      const firstContactId = contactIds[0];
      const firstContactName = this.getNameForPersonWithId(firstContactId);
      if (contactIds.length === 1) {
        return firstContactName;
      } else {
        return firstContactName + ' ...';
      }
    }
  }

  getNameForPersonWithId(personId) {
    const person = DA.getPersonById(personId);
    return person.preferredFullName;
  }

  nameOfOtherPersonToCall(call) {
    const otherPersonId = call.otherPersonId;
    if (otherPersonId === 0) {
      const senderFirm = DA.getSenderFirmOfOutreachWithCallRailCall(call.callRailCallId);
      if (senderFirm) {
        return senderFirm.name;
      } else {
        return 'Unknown Person';
      }
    } else {
      const person = DA.getPersonById(otherPersonId);
      return person ? person.preferredFullName : 'Unknown Person';
    }
  }

  phoneNumberStringForPhoneNumberObject(phoneNumberObj) {
    const rolesStr = phoneNumberObj.roles;
    if (!rolesStr) {
      console.log(`PhoneNumber object with ID ${phoneNumberObj.phoneId} has null or empty roles string`);
      return phoneNumberObj.number;
    }
    const rolesArr = rolesStr.split(',');
    const firstLetterArray = rolesArr.map((roleStr) => roleStr.substring(0, 1).toLowerCase());
    const shortRolesStr = firstLetterArray.join(',');
    const parenthesizedShorRolesStr = '(' + shortRolesStr + ')';
    return phoneNumberObj.number + ' ' + parenthesizedShorRolesStr;
  }

  dateTimeToDateSpan(dt) {
    return (
      <span>
        {dateStringForDateTime(dt)}
        <br />
        {timeStringForDateTime(dt)}
      </span>
    );
  }

  twoDigitStringForNumber(num) {
    if (num >= 10) {
      return '' + num;
    } else {
      return '0' + num;
    }
  }

  secondsToDurationString(secondsString) {
    const seconds = parseInt(secondsString);
    if (seconds < 60) {
      return '0:' + this.twoDigitStringForNumber(seconds);
    } else {
      const secsPart = seconds % 60;
      const minPart = (seconds - secsPart) / 60;
      return `${minPart}:${this.twoDigitStringForNumber(secsPart)}`;
    }
  }

  handleStartDateChange(newDate) {
    console.log(`new startDate: ${newDate.toISODate()}`);
    this.setState({ startDate: newDate }, () => this.sanityCheckDatesAndFetchDataAndDisplay());
    this.props.updateCallLogFilter({ filterItemName: 'startDate', value: newDate.toISO() });
  }

  handleEndDateChange(newDate) {
    console.log(`new endDate: ${newDate.toISODate()}`);
    this.setState({ endDate: newDate }, () => this.sanityCheckDatesAndFetchDataAndDisplay());
    this.props.updateCallLogFilter({ filterItemName: 'endDate', value: newDate.toISO() });
  }

  showUnknownCallerFilter = (event) => {
    const { clientX, clientY } = event;
    this.setState({ shouldShowUnknownCallerFilter: true, filterX: clientX, filterY: clientY });
  };

  handleUnknownCallersFilterChange = (event) => {
    this.setState({ showUnknownCallers: event.target.checked });
  };

  handleUnknownCallerFilterClickAway = () => {
    this.setState({ shouldShowUnknownCallerFilter: false });
  };

  showDirectionFilter = (event) => {
    const { clientX, clientY } = event;
    this.setState({ shouldShowDirectionFilter: true, filterX: clientX, filterY: clientY });
  };

  handleInboundFilterChange = (event) => {
    const newValue = event.target.checked;
    const payload = {
      pageName: 'callLogPage',
      filterName: 'direction',
      propertyName: 'incoming',
      value: newValue,
    };
    this.props.setPropertyFilterState(payload);
    this.setState({ showInboundCalls: newValue });
  };

  handleOutboundFilterChange = (event) => {
    const newValue = event.target.checked;
    const payload = {
      pageName: 'callLogPage',
      filterName: 'direction',
      propertyName: 'outgoing',
      value: newValue,
    };
    this.props.setPropertyFilterState(payload);
    this.setState({ showOutboundCalls: newValue });
  };

  handleDirectionFilterClickAway = () => {
    this.setState({ shouldShowDirectionFilter: false });
  };

  handleAutocompleteChange(name) {
    if (name) {
      this.setState({ otherPersonNameFilter: name });
    } else {
      this.setState({ otherPersonNameFilter: null });
    }
  }

  placeCall(numberToCall) {
    showAlert(this, 'Outbound calling is not yet supported.');
  }

  showNoteModal(call) {
    this.callRailCallId = call.callRailCallId;
    const note = DA.getNoteAboutCallRailCallWithId(this.callRailCallId);
    const noteText = note ? note.content : '';
    this.noteId = note ? note.noteId : 0;
    this.oldNoteText = noteText;

    this.setState({
      noteText: noteText,
      shouldShowNoteModal: true,
    });
  }

  showConversionModal(call) {
    if (call.isConverted) {
      return showAlert(this, `This logged call has already been converted to a Lead or Prospect.`);
    }

    if (call.otherPersonId > 0) {
      return showAlert(this, `You can’t convert calls from a known person to a Lead or Prospect yet.`);
    }

    this.callRailCallId = call.callRailCallId;

    this.setState({
      shouldShowConversionModal: true,
    });
  }

  handleNewNoteText(newText) {
    this.saveNoteToDb(newText);
    this.setState({ shouldShowNoteModal: false });
  }

  saveNoteToDb(noteText) {
    if (this.noteId === 0) {
      this.saveAddedNoteToDb(noteText);
    } else if (this.oldNoteText !== noteText) {
      this.saveUpdatedNoteToDb(this.noteId, noteText);
    }
  }

  saveAddedNoteToDb(noteText) {
    const uri = `/api/note`;
    const apiStruct = this.buildApiStructForCallWithNote(noteText);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (note) => {
      ReduxLoader.addOrUpdateNotesOnCallRailCalls([note]);
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  saveUpdatedNoteToDb(noteId, noteText) {
    const uri = `/api/note/${noteId}`;
    const apiStruct = this.buildApiStructForCallWithNote(noteText);
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (note) => {
      ReduxLoader.addOrUpdateNotesOnCallRailCalls([note]);
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  buildApiStructForCallWithNote(noteText) {
    const authorId = this.userPersonId;
    const viewingFirmId = this.viewingFirmId;
    const date = this.now.toISO();
    const callRailCallId = this.callRailCallId;
    const noteId = this.noteId;

    return {
      noteId,
      callRailCallId,
      content: noteText,
      date,
      authorId,
      viewingFirmId,
    };
  }

  handleConversionTypeChange = (event) => {
    this.setState({ conversionType: event.target.value });
  };

  convertCall() {
    const conversionType = this.state.conversionType;
    if (conversionType === 'prospect') {
      const primingInfo = {
        date: DateTime.local().toISO(),
        inquiryId: 0,
        callRailCallId: this.callRailCallId,
        disposition: conversionType,
      };
      this.props.setInitialCallFormPrimingInfo(primingInfo);
      this.setState({ redirect: 'newProspectPage' });
    } else {
      this.setState({ shouldShowNewInquiryModal: true });
    }
    this.setState({ shouldShowConversionModal: false });
  }

  closeInquiryModalAndClearCallSelection() {
    this.callRailCallId = 0;
    this.setState({ shouldShowNewInquiryModal: false });
  }

  shouldHighlightFilterIcon(columnId) {
    if (columnId === 'otherPersonName') {
      return this.shouldHighlightOtherPartyFilterIcon();
    } else if (columnId === 'direction') {
      return this.shouldHighlightDirectionFilterIcon();
    }
  }

  shouldHighlightOtherPartyFilterIcon() {
    return !this.otherPartyFilterMenuIsInDefaultState();
  }

  otherPartyFilterMenuIsInDefaultState() {
    return this.state.showUnknownCallers;
  }

  shouldHighlightDirectionFilterIcon() {
    return !this.directionFilterMenuIsInDefaultState();
  }

  directionFilterMenuIsInDefaultState() {
    return this.state.showInboundCalls && this.state.showOutboundCalls;
  }

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;
    const { order, orderBy, startDate, endDate, calls } = this.state;
    const filteredCalls = this.applyFilter(calls);
    const summary = `Calls Logged (${filteredCalls.length})`;
    const namesOfOtherPeople = calls.map((call) => call.otherPersonName);
    const deDupedNames = new Set(namesOfOtherPeople);
    const deDupedArray = [...deDupedNames];
    const sortedNames = deDupedArray.sort();

    const StyledMenuDialog = styled(Dialog)`
      .MuiBackdrop-root {
        background-color: transparent;
      },
    `;

    const filterPosition = {
      position: 'absolute',
      left: this.state.filterX + 'px',
      top: this.state.filterY + 'px',
      margin: '5px',
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    };
    const filterStyle = { style: filterPosition };

    return (
      <div>
        <Paper className={classes.root}>
          <StyledAppBar
            summary={summary}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(newDate) => this.handleStartDateChange(newDate)}
            onEndDateChange={(newDate) => this.handleEndDateChange(newDate)}
            autocompleteOptions={sortedNames}
            onAutocompleteChange={(name) => this.handleAutocompleteChange(name)}
          />
          <div className={classes.tableWrapper}>
            <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />
            <Table stickyHeader className={classes.table}>
              <StyledTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={this.handleRequestSort}
                onShowUnknownCallerFilter={this.showUnknownCallerFilter}
                onShowDirectionFilter={this.showDirectionFilter}
                shouldHighlightFilterIcon={(columnId) => this.shouldHighlightFilterIcon(columnId)}
              />
              <TableBody>
                {stableSort(filteredCalls, getSorting(order, orderBy)).map((call, i) => {
                  return (
                    <TableRow
                      key={call.callRailCallId}
                      tabIndex={-1}
                      id={'call-row-' + call.callRailCallId}
                      style={{ backgroundColor: i % 2 ? '#f7f7f7' : '#fff' }}
                    >
                      <TableCell
                        className={cx([classes.clickableCell, { [classes.inactiveName]: call.inactive }])}
                        component="th"
                        scope="row"
                        padding="normal"
                        onClick={(event) => this.handleContactClick(event, call)}
                      >
                        {call.otherPersonName}
                      </TableCell>
                      <TableCell className={cx([{ [classes.inactiveName]: call.inactive }])}>
                        {this.dateTimeToDateSpan(call.date)}
                      </TableCell>
                      <TableCell>{convertToUserStdPhoneNumber(call.otherNumber)}</TableCell>
                      <TableCell>{call.direction}</TableCell>
                      <TableCell>{this.secondsToDurationString(call.recordingDuration)}</TableCell>
                      <TableCell style={{ width: '332px' }}>
                        <CallRailPlayerWidget
                          firmId={call.firmId}
                          crCallId={call.crResourceId}
                          redirectUri={call.recordingUrl}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          id="tooltip-convert"
                          key={0}
                          title="Add Note"
                          placement="top"
                          classes={{ tooltip: classes.tooltip }}
                        >
                          <Button
                            color="primary"
                            className={classes.actionButton}
                            onClick={() => this.showNoteModal(call)}
                          >
                            <EditIcon className={classes.icon} />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          id="tooltip-convert"
                          key={0}
                          title={call.isConverted ? '' : 'Convert to Lead or Prospect'}
                          placement="top"
                          classes={{ tooltip: classes.tooltip }}
                        >
                          <Button
                            color="primary"
                            className={classes.actionButton}
                            onClick={() => this.showConversionModal(call)}
                          >
                            <ConvertIcon
                              className={cx(classes.availableConvertIcon, {
                                [classes.grayedConvertIcon]: call.isConverted,
                              })}
                            />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filteredCalls.length === 0 && (
              <div className={classes.noCallsNotice}>No calls match the current filters and date range</div>
            )}
          </div>
        </Paper>

        <StyledMenuDialog
          open={this.state.shouldShowUnknownCallerFilter}
          PaperComponent={Paper}
          PaperProps={filterStyle}
          onEntered={handleEntered}
        >
          <ClickAwayListener onClickAway={() => this.handleUnknownCallerFilterClickAway()}>
            <List>
              <ListItem className={classes.filterListItem}>
                <Typography variant="caption" display="block" gutterBottom>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showUnknownCallers}
                        onChange={(event) => this.handleUnknownCallersFilterChange(event)}
                        value="Show Unknown Callers"
                        color="primary"
                      />
                    }
                    label="Show Unknown Callers"
                    className={classes.filterListLabel}
                  />
                </Typography>
              </ListItem>
            </List>
          </ClickAwayListener>
        </StyledMenuDialog>

        <StyledMenuDialog
          open={this.state.shouldShowDirectionFilter}
          PaperComponent={Paper}
          PaperProps={filterStyle}
          onEntered={handleEntered}
        >
          <ClickAwayListener onClickAway={() => this.handleDirectionFilterClickAway()}>
            <List>
              <ListItem className={classes.filterListItem}>
                <Typography variant="caption" display="block" gutterBottom>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showInboundCalls}
                        onChange={(event) => this.handleInboundFilterChange(event)}
                        value="Inbound"
                        color="primary"
                      />
                    }
                    label="Inbound"
                    className={classes.filterListLabel}
                  />
                </Typography>
              </ListItem>
              <ListItem className={classes.filterListItem}>
                <Typography variant="caption" display="block" gutterBottom>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showOutboundCalls}
                        onChange={(event) => this.handleOutboundFilterChange(event)}
                        value="Outbound"
                        color="primary"
                      />
                    }
                    label="Outbound"
                    className={classes.filterListLabel}
                  />
                </Typography>
              </ListItem>
            </List>
          </ClickAwayListener>
        </StyledMenuDialog>

        <NoteEditingModal
          show={this.state.shouldShowNoteModal}
          initialValue={this.state.noteText}
          onClose={() => this.setState({ shouldShowNoteModal: false })}
          onSave={(newText) => this.handleNewNoteText(newText)}
        />

        <Modal
          open={this.state.shouldShowConversionModal}
          title="Convert Call"
          okButtonLabel="Convert"
          cancelButtonLabel="Cancel"
          onOk={() => this.convertCall()}
          onCancel={() => this.setState({ shouldShowConversionModal: false })}
        >
          <div>
            <div className={classes.queryDiv}>Would you like to convert this call to a</div>
            <div className={classes.radioButtonDiv}>
              <FormControl variant="standard" component="fieldset" className={classes.formControl}>
                <RadioGroup
                  aria-label="Contact Type"
                  name="contact-type"
                  value={this.state.conversionType}
                  onChange={this.handleConversionTypeChange}
                >
                  <FormControlLabel value="lead" control={<CrmRadio />} label="Lead" />
                  <FormControlLabel value="prospect" control={<CrmRadio />} label="Prospect" />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        </Modal>

        <NewInquiryModal
          show={this.state.shouldShowNewInquiryModal}
          closeModalFunc={() => this.closeInquiryModalAndClearCallSelection()}
          callRailCallId={this.callRailCallId}
        />
      </div>
    );
  }
}

class NoteEditingModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      myText: props.initialValue,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.initialValue !== this.props.initialValue) {
      this.setState({ myText: this.props.initialValue });
    }
  }

  restoreOriginalTextAndClose() {
    this.setState({ myText: this.props.initialValue });
    this.props.onClose();
  }

  restoreOriginalTextAndSave(text) {
    this.setState({ myText: this.props.initialValue });
    this.props.onSave(text);
  }

  render() {
    return (
      <Dialog
        open={this.props.show}
        onClose={() => this.restoreOriginalTextAndClose()}
        fullWidth={true}
        maxWidth={'sm'}
      >
        <DialogTitle>Add Note</DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CrmTextField
                label="Note"
                fullWidth
                multiline
                value={this.state.myText}
                onChange={(event) => this.setState({ myText: event.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <CrmButton outlined onClick={() => this.restoreOriginalTextAndClose()}>
            Cancel
          </CrmButton>
          <CrmButton filled onClick={() => this.restoreOriginalTextAndSave(this.state.myText)}>
            Save
          </CrmButton>
        </DialogActions>
      </Dialog>
    );
  }
}

CallRailCallTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  reduxFiltersState: state.intendedInfo.callLogPageFilters,
  reduxPropertyFiltersState: state.filterStates.propertyFilters,
});

const actionProducers = {
  setApiResponseInfo,
  setIntendedReferrerContactId,
  setIntendedReferrerCompanyId,
  setCaseIntendedClientId,
  setIntendedEmergencyContactId,
  setIntendedClientId,
  updateCallLogFilter,
  setPropertyFilterState,
  setInitialCallFormPrimingInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(CallRailCallTable));
